export interface APIErrorPayload {
  code?: string;
  message?: string;
  requestId?: string;
  [key: string]: unknown;
}

export class APIError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId: string | undefined;
  readonly details: APIErrorPayload;

  constructor(status: number, details: APIErrorPayload) {
    super(details.message ?? `Request failed with status ${status}`);
    this.name = "APIError";
    this.status = status;
    this.code = details.code ?? "request_failed";
    this.requestId = details.requestId;
    this.details = details;
  }
}

function cookie(name: string): string {
  const match = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : "";
}

function csrfToken(): string {
  return cookie("__Host-venueops_csrf") || cookie("venueops_csrf");
}

function isUnsafe(method: string): boolean {
  return !["GET", "HEAD", "OPTIONS"].includes(method.toUpperCase());
}

/**
 * Whether the selected workspace is currently read-only, mirrored here from
 * GET /api/v1/tenant/access by app/workspaceAccess.tsx.
 *
 * The server is the enforcement and refuses every one of these writes on its own. This exists so
 * the refusal is immediate and uniform: one place decides, rather than forty screens each
 * remembering to disable their own controls, and the screen that forgot being the one that lets
 * somebody fill in a long form before the server throws it away.
 *
 * Module state rather than React context because api() is a plain function called from everywhere,
 * including from code that is not rendering.
 */
let readOnlyWorkspace = false;

export function setWorkspaceReadOnly(value: boolean): void {
  readOnlyWorkspace = value;
}

/**
 * The paths a read-only workspace may still write to.
 *
 * Mirrors billingWallAllows and the administrative branch of entitlementAllows in
 * edge/api/src/modules/billing/entitlement-gate.ts: settling the subscription, signing in and out,
 * recording setup progress, the legal obligations that outlive a subscription, and the platform
 * operator console, which belongs to us rather than to any one workspace.
 */
function readOnlyExempt(path: string): boolean {
  const route = path.split("?")[0] ?? path;
  return (
    route.startsWith("/api/v1/auth") ||
    route.startsWith("/api/v1/billing") ||
    route.startsWith("/api/v1/operator") ||
    route.startsWith("/api/v1/privacy") ||
    route.startsWith("/api/v1/support-access") ||
    route.startsWith("/api/v1/analytics/exports") ||
    route.startsWith("/api/v1/tenant/onboarding") ||
    route.startsWith("/api/v1/tenant/access")
  );
}

export async function api<T>(
  path: string,
  options: RequestInit & { token?: string } = {},
): Promise<T> {
  const method = options.method ?? "GET";
  if (isUnsafe(method) && readOnlyWorkspace && !readOnlyExempt(path)) {
    // Shaped exactly like the server's refusal, so every screen that already reports an APIError
    // reports this one the same way, and nothing has to learn a second vocabulary for it.
    throw new APIError(402, {
      code: "subscription_restricted",
      message:
        "This workspace is read-only until its subscription is reactivated. Everything is still here, and nothing can be changed.",
    });
  }
  const headers = new Headers(options.headers);
  const bodyIsForm = options.body instanceof FormData;
  if (options.body && !bodyIsForm && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  if (isUnsafe(method)) {
    const csrf = csrfToken();
    if (csrf) headers.set("X-CSRF-Token", csrf);
  }
  if (options.token) headers.set("Authorization", `Bearer ${options.token}`);
  headers.set("Accept", "application/json");

  const response = await fetch(path, {
    ...options,
    method,
    headers,
    credentials: "include",
  });

  if (!response.ok) {
    let details: APIErrorPayload = {};
    try {
      const decoded = (await response.json()) as { error?: APIErrorPayload };
      details = decoded.error ?? {};
    } catch {
      details = { message: "The server returned an unreadable response." };
    }
    throw new APIError(response.status, details);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export function jsonBody(value: unknown): string {
  return JSON.stringify(value);
}

export function queryString(values: Record<string, string | number | boolean | undefined>): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(values)) {
    if (value !== undefined && value !== "") query.set(key, String(value));
  }
  const encoded = query.toString();
  return encoded ? `?${encoded}` : "";
}

export async function download(path: string): Promise<void> {
  const headers = new Headers({ Accept: "application/octet-stream" });
  const response = await fetch(path, { headers, credentials: "include" });
  if (!response.ok) throw new APIError(response.status, { message: "Download failed." });
  const blob = await response.blob();
  const disposition = response.headers.get("Content-Disposition") ?? "";
  const filename = disposition.match(/filename="?([^";]+)"?/i)?.[1] ?? "venueops-export";
  const objectURL = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectURL;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(objectURL);
}
